import * as XLSX from "xlsx";

// ─── helpers ────────────────────────────────────────────────────────────────

function n(v) { return Number(v || 0); }

function stuName(stu) {
  if (!stu) return "—";
  return `${stu.firstName || ""} ${stu.lastName || ""}`.trim() || `#${stu.id}`;
}

function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return isNaN(d) ? "" : d.toLocaleDateString("en-PH", { year: "numeric", month: "short", day: "numeric" });
}

function setColWidths(ws, widths) {
  ws["!cols"] = widths.map(wch => ({ wch }));
}

function headerRow(cells) {
  return cells;
}

/**
 * Appends an aoa to a worksheet starting at the next row after existing data.
 * Returns the new worksheet (same reference).
 */
function appendRows(ws, rows, origin) {
  XLSX.utils.sheet_add_aoa(ws, rows, { origin });
  return ws;
}

// ─── main export ─────────────────────────────────────────────────────────────

/**
 * Generates a multi-sheet Excel workbook with the full financial computation
 * breakdown and triggers a browser download.
 *
 * @param {object} opts
 * @param {object} opts.kpis       - financeKPIs from AdminDashboard
 * @param {object} opts.rawData    - { invoices, payments, allPayments, employees, feeStructures, sections, students, gradeLevels }
 * @param {object} opts.activeSy   - active school year object
 * @param {object} opts.activeSem  - active semester object
 */
export function exportFinancialReport({ kpis, rawData, activeSy, activeSem }) {
  const {
    invoices     = [],
    payments     = [],   // scoped payments (linked to scoped invoices)
    allPayments  = [],   // all-time payments (for per-invoice outstanding balance)
    employees    = [],
    feeStructures = [],
    sections     = [],
    students     = [],
    gradeLevels  = [],
  } = rawData;

  // ── lookup maps ────────────────────────────────────────────────────────
  const stuMap   = new Map(students.map(x    => [String(x.id), x]));
  const secMap   = new Map(sections.map(x    => [String(x.id), x]));
  const gradeMap = new Map(gradeLevels.map(x => [String(x.id), x]));
  const invMap   = new Map(invoices.map(x    => [String(x.id), x]));

  const feeMap = new Map();
  for (const fs of feeStructures) {
    feeMap.set(`${fs.gradeLevelId}|${fs.schoolYearId}`, n(fs.totalAmount));
  }

  const glOf = inv => {
    const stu = stuMap.get(String(inv.studentId));
    const sec = stu?.currentSectionId ? secMap.get(String(stu.currentSectionId)) : null;
    return sec?.gradeLevelId ? gradeMap.get(String(sec.gradeLevelId)) : null;
  };

  const feeOf = inv => {
    const stu = stuMap.get(String(inv.studentId));
    const sec = stu?.currentSectionId ? secMap.get(String(stu.currentSectionId)) : null;
    const key = sec?.gradeLevelId && inv.schoolYearId ? `${sec.gradeLevelId}|${inv.schoolYearId}` : null;
    return key ? feeMap.get(key) : undefined;
  };

  const secLabelOf = stu => {
    if (!stu?.currentSectionId) return "—";
    const sec = secMap.get(String(stu.currentSectionId));
    return sec ? (sec.name || sec.label || `Section #${sec.id}`) : "—";
  };

  const effDateMs = p => p.paymentReceivedOn
    ? new Date(p.paymentReceivedOn).getTime()
    : new Date(p.paidAt || 0).getTime();

  const effDateLabel = p => p.paymentReceivedOn
    ? fmtDate(p.paymentReceivedOn)
    : (p.paidAt ? fmtDate(p.paidAt) : "—");

  const scopeLabel = [activeSy?.label, activeSem?.label].filter(Boolean).join(" · ") || "All";
  const generatedAt = new Date().toLocaleString("en-PH");
  const wb = XLSX.utils.book_new();

  // ═══════════════════════════════════════════════════════════════════════
  // SHEET 1 — SUMMARY
  // ═══════════════════════════════════════════════════════════════════════
  const summaryAoa = [
    ["FINANCIAL OVERVIEW REPORT — COMPUTATION AUDIT"],
    [`Scope : ${scopeLabel}`],
    [`Generated : ${generatedAt}`],
    [],
    // ── KPI table ──
    ["#", "Metric",                    "Amount (PHP)",              "Description"],
    ["1", "Grand Total",               n(kpis.grandTotal),          "All school fees before discounts"],
    ["2", "Gross Total",               n(kpis.grossTotal),          "After student discounts applied"],
    ["3", "Total Payments",            n(kpis.totalPayments),       "Collected in this scope"],
    ["4", "Total Expenses",            n(kpis.totalExpenses),       "Monthly payroll (active staff)"],
    ["5", "Current Deposits",          n(kpis.currentDeposits),     "Total Payments − Total Expenses"],
    ["6", "Remaining Collectibles",    n(kpis.remainingCollectibles),"Per-invoice balance, floored at 0, summed — same as Outstanding Receivables"],
    [],
    // ── formula notes ──
    ["FORMULA NOTES"],
    ["Grand Total",
     "For each scoped invoice: use the Grade Level's fee structure amount for that SY. Falls back to invoice.amount if no fee structure is found."],
    ["Gross Total",
     "Sum of all scoped invoice amounts. Invoice amounts already reflect per-student discounts granted at invoice creation."],
    ["Total Payments",
     "Sum of all payment records whose invoiceId links to a scoped invoice."],
    ["Total Expenses",
     "Sum of monthly salary of every active employee (status ≠ Inactive, salary > 0). Not scoped — reflects the full payroll snapshot."],
    ["Current Deposits",
     "Total Payments − Total Expenses. Can be negative (deficit) if payroll exceeds collections."],
    ["Remaining Collectibles",
     "For each scoped invoice: MAX(0, invoice amount − payments on it), summed. What students still owe — an overpaid invoice contributes 0 and never offsets another student's balance."],
    [],
    ["See the other sheets for the full row-level data behind each metric."],
  ];

  const ws1 = XLSX.utils.aoa_to_sheet(summaryAoa);
  setColWidths(ws1, [4, 28, 20, 72]);
  XLSX.utils.book_append_sheet(wb, ws1, "Summary");

  // ═══════════════════════════════════════════════════════════════════════
  // SHEET 2 — GRAND TOTAL  (per-invoice fee structure lookup)
  // ═══════════════════════════════════════════════════════════════════════
  const grandHeader = [
    "Invoice ID", "Student #", "Student Name", "Section",
    "Grade Level", "Standard Fee", "Invoice Amount",
    "Amount Counted", "Basis",
  ];
  const grandRows = [grandHeader];
  let grandRunning = 0;

  for (const inv of invoices) {
    const stu     = stuMap.get(String(inv.studentId));
    const gl      = glOf(inv);
    const fsAmt   = feeOf(inv);              // undefined if no fee structure
    const invAmt  = n(inv.amount);
    const used    = fsAmt != null ? fsAmt : invAmt;
    const source  = fsAmt != null ? "Fee Structure" : "Fallback: Invoice Amount";
    grandRunning += used;

    grandRows.push([
      inv.id,
      stu?.studentNumber || stu?.id || "—",
      stuName(stu),
      secLabelOf(stu),
      gl?.name || "Unclassified",
      fsAmt != null ? fsAmt : "",   // blank when not applicable
      invAmt,
      used,
      source,
    ]);
  }

  grandRows.push(
    [],
    ["", "", "", "", "", "", "GRAND TOTAL", grandRunning, ""],
  );

  const ws2 = XLSX.utils.aoa_to_sheet(grandRows);
  setColWidths(ws2, [12, 14, 30, 20, 22, 22, 24, 28, 34]);
  XLSX.utils.book_append_sheet(wb, ws2, "Grand Total");

  // ═══════════════════════════════════════════════════════════════════════
  // SHEET 3 — GROSS TOTAL  (all scoped invoices)
  // ═══════════════════════════════════════════════════════════════════════
  const grossHeader = [
    "Invoice ID", "Student #", "Student Name", "Grade Level",
    "Invoice Amount", "Discount (Grand − Gross)", "Status", "Due Date",
  ];
  const grossRows = [grossHeader];
  let grossRunning = 0;

  const sortedByGrade = [...invoices].sort((a, b) => {
    const gA = glOf(a)?.name || "zzz";
    const gB = glOf(b)?.name || "zzz";
    return gA.localeCompare(gB);
  });

  for (const inv of sortedByGrade) {
    const stu    = stuMap.get(String(inv.studentId));
    const gl     = glOf(inv);
    const fsAmt  = feeOf(inv);
    const invAmt = n(inv.amount);
    const discount = fsAmt != null ? Math.max(0, fsAmt - invAmt) : "";
    grossRunning += invAmt;

    grossRows.push([
      inv.id,
      stu?.studentNumber || stu?.id || "—",
      stuName(stu),
      gl?.name || "Unclassified",
      invAmt,
      discount,
      inv.status || "—",
      fmtDate(inv.dueDate),
    ]);
  }

  grossRows.push(
    [],
    ["", "", "", "GROSS TOTAL", grossRunning, "", "", ""],
  );

  const ws3 = XLSX.utils.aoa_to_sheet(grossRows);
  setColWidths(ws3, [12, 14, 30, 22, 18, 24, 12, 16]);
  XLSX.utils.book_append_sheet(wb, ws3, "Gross Total");

  // ═══════════════════════════════════════════════════════════════════════
  // SHEET 4 — TOTAL PAYMENTS  (all scoped payments, newest first)
  // ═══════════════════════════════════════════════════════════════════════
  const payHeader = [
    "OR #", "Student #", "Student Name", "Grade Level",
    "Month", "Cash", "Online / Transfer", "Total Amount",
    "Reference #", "Method", "Date Received", "Date Recorded (Input)",
  ];
  const payRows = [payHeader];
  let payRunning   = 0;
  let cashRunning  = 0;
  let onlineRunning = 0;

  const sortedPay = [...payments].sort((a, b) => effDateMs(b) - effDateMs(a));

  for (const p of sortedPay) {
    const inv    = invMap.get(String(p.invoiceId));
    const stu    = inv ? stuMap.get(String(inv.studentId)) : null;
    const gl     = inv ? glOf(inv) : null;
    const amt    = n(p.amount);
    const isCash = (p.method || "").toLowerCase() === "cash";
    const effDate = p.paymentReceivedOn || p.paidAt;
    const month   = effDate
      ? new Date(effDate).toLocaleDateString("en-PH", { year: "numeric", month: "long" })
      : "—";

    payRunning += amt;
    if (isCash) cashRunning   += amt;
    else        onlineRunning += amt;

    payRows.push([
      p.id,
      stu?.studentNumber || stu?.id || "—",
      stuName(stu),
      gl?.name || "Unclassified",
      month,
      isCash ? amt : "",
      isCash ? "" : amt,
      amt,
      p.bankName || "",
      p.method   || "—",
      p.paymentReceivedOn ? fmtDate(p.paymentReceivedOn) : "—",
      p.paidAt            ? fmtDate(p.paidAt)            : "—",
    ]);
  }

  payRows.push(
    [],
    ["", "", "", "", "TOTAL", cashRunning, onlineRunning, payRunning, "", "", "", ""],
  );

  const ws4 = XLSX.utils.aoa_to_sheet(payRows);
  setColWidths(ws4, [12, 14, 30, 22, 20, 16, 20, 16, 18, 16, 20, 24]);
  XLSX.utils.book_append_sheet(wb, ws4, "Total Payments");

  // ═══════════════════════════════════════════════════════════════════════
  // SHEET 5 — TOTAL EXPENSES  (active staff payroll snapshot)
  // ═══════════════════════════════════════════════════════════════════════
  const expHeader = [
    "Employee Name", "Type / Role", "Status", "Monthly Salary (PHP)",
  ];
  const expRows = [expHeader];
  let expRunning = 0;

  const activeStaff = [...employees]
    .filter(e => (e.status || "Active") !== "Inactive" && n(e.salary) > 0)
    .sort((a, b) => n(b.salary) - n(a.salary));

  for (const e of activeStaff) {
    const name   = (e.firstName && e.lastName)
      ? `${e.firstName} ${e.lastName}`.trim()
      : (e.name || `#${e.id}`);
    const salary = n(e.salary);
    expRunning  += salary;

    expRows.push([
      name,
      e.employeeType || e.type || "Staff",
      e.status || "Active",
      salary,
    ]);
  }

  expRows.push(
    [],
    ["", "", "TOTAL EXPENSES", expRunning, ""],
    [],
    ["Note: Expenses are NOT scoped to the selected SY/Semester.",
     "They represent the full active-staff payroll snapshot at the time of export."],
  );

  const ws5 = XLSX.utils.aoa_to_sheet(expRows);
  setColWidths(ws5, [32, 22, 14, 24]);
  XLSX.utils.book_append_sheet(wb, ws5, "Total Expenses");

  // ═══════════════════════════════════════════════════════════════════════
  // SHEET 6 — REMAINING COLLECTIBLES  (per-invoice outstanding balance)
  // ═══════════════════════════════════════════════════════════════════════

  // Build per-invoice paid amounts using ALL payments (all-time),
  // matching what the detail modal shows. The KPI uses scoped payments only
  // (globalFormula); per-invoice totals here may differ — a note explains why.
  const paidByInv = new Map();
  for (const p of allPayments) {
    const k = String(p.invoiceId);
    paidByInv.set(k, (paidByInv.get(k) || 0) + n(p.amount));
  }

  const colHeader = [
    "Invoice ID", "Student #", "Student Name", "Grade Level",
    "Invoice Amount", "Total Paid (all-time)", "Balance Remaining", "Status",
  ];
  const colRows = [colHeader];
  let colRunning = 0;

  // Sort: highest balance first so the biggest debts are at the top.
  const sortedCol = [...invoices].sort((a, b) => {
    const remA = Math.max(0, n(a.amount) - (paidByInv.get(String(a.id)) || 0));
    const remB = Math.max(0, n(b.amount) - (paidByInv.get(String(b.id)) || 0));
    return remB - remA;
  });

  for (const inv of sortedCol) {
    const stu       = stuMap.get(String(inv.studentId));
    const gl        = glOf(inv);
    const invAmt    = n(inv.amount);
    const paid      = paidByInv.get(String(inv.id)) || 0;
    const remaining = Math.max(0, invAmt - paid);
    colRunning     += remaining;

    colRows.push([
      inv.id,
      stu?.studentNumber || stu?.id || "—",
      stuName(stu),
      gl?.name || "Unclassified",
      invAmt,
      paid,
      remaining,
      inv.status || "—",
    ]);
  }

  colRows.push(
    [],
    ["", "", "", "", "", "", colRunning, "← Per-invoice sum (all-time payments)"],
    ["", "", "", "", "", "", n(kpis.remainingCollectibles), "← KPI value (per-invoice sum, scoped payments only)"],
    [],
    ["Note: The two totals above may differ when some payments fall outside",
     "the selected SY/Semester scope. Both use the same per-invoice method",
     "(balance floored at zero per invoice), so an overpaid invoice never",
     "offsets another student's unpaid balance."],
    ["The KPI on the dashboard uses scoped payments only.",
     "This sheet shows all-time payments per invoice for a complete picture."],
  );

  const ws6 = XLSX.utils.aoa_to_sheet(colRows);
  setColWidths(ws6, [12, 14, 30, 22, 18, 22, 20, 12]);
  XLSX.utils.book_append_sheet(wb, ws6, "Remaining Collectibles");

  // ═══════════════════════════════════════════════════════════════════════
  // SHEET 7 — CURRENT DEPOSITS  (computation)
  // ═══════════════════════════════════════════════════════════════════════
  const depAoa = [
    ["CURRENT DEPOSITS — COMPUTATION"],
    [],
    ["Item",                            "Amount (PHP)"],
    ["(A) Total Payments collected",    n(kpis.totalPayments)],
    ["(B) Total Expenses (payroll)",    n(kpis.totalExpenses)],
    [],
    ["Current Deposits  =  A − B",      n(kpis.currentDeposits)],
    [],
    ["If negative, expenses exceed collections for this period."],
    ["Payroll expenses are not scoped to SY/Semester (full snapshot)."],
  ];

  const ws7 = XLSX.utils.aoa_to_sheet(depAoa);
  setColWidths(ws7, [42, 20]);
  XLSX.utils.book_append_sheet(wb, ws7, "Current Deposits");

  // ═══════════════════════════════════════════════════════════════════════
  // DOWNLOAD
  // ═══════════════════════════════════════════════════════════════════════
  const safeScope = scopeLabel
    .replace(/[\\/:*?"<>|]/g, "-")
    .replace(/·/g, "-")
    .replace(/\s+/g, "_");
  const dateStamp = new Date().toISOString().slice(0, 10);
  XLSX.writeFile(wb, `Financial_Report_${safeScope}_${dateStamp}.xlsx`);
}
